import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Checkbox, FormControlLabel, Fade, Alert } from '@mui/material';
import { CheckCircle, Cancel, ArrowForward } from '@mui/icons-material';

interface EligibilityChecklistProps {
  track: 'cloud' | 'data';
  onComplete: () => void;
  onBack: () => void;
}

const EligibilityChecklist: React.FC<EligibilityChecklistProps> = ({ track, onComplete, onBack }) => {
  const [step, setStep] = useState<'disclaimer' | 'question1' | 'question2' | 'agreement' | 'ineligible'>('disclaimer');
  const [agreementChecked, setAgreementChecked] = useState(false);

  const handleAnswer = (answer: boolean, nextStep: 'question2' | 'agreement' | 'ineligible') => {
    if (!answer) {
      setStep('ineligible');
    } else {
      setStep(nextStep);
    }
  };

  const handleSubmit = () => {
    if (agreementChecked) {
      onComplete();
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="caption"
          sx={{
            fontFamily: '"Fira Code", "JetBrains Mono", monospace',
            color: 'rgba(103,232,249,0.6)',
            fontSize: '0.75rem',
            letterSpacing: '1px',
            mb: 1,
            display: 'block',
          }}
        >
          {'>'} STEP 1: ELIGIBILITY VERIFICATION
        </Typography>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            color: '#f1f5f9',
            letterSpacing: '-0.5px',
          }}
        >
          Eligibility Checklist
        </Typography>
      </Box>

      {/* Disclaimer */}
      {step === 'disclaimer' && (
        <Fade in timeout={600}>
          <Paper
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 2,
              border: '1px solid rgba(34,211,238,0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(34,211,238,0.3)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Important Notice
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You will be required to provide documented proof of all answers provided in this eligibility check upon submission.
              </Typography>
            </Alert>
            
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              Before proceeding with the assessment, we need to verify your eligibility. 
              Please answer the following questions honestly and accurately.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => setStep('question1')}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  borderRadius: 4,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)',
                  color: '#0a0a0a',
                  boxShadow: '0 4px 20px rgba(245,158,11,0.3)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)',
                    boxShadow: '0 12px 32px rgba(245,158,11,0.5)',
                  }
                }}
              >
                Begin Eligibility Check
              </Button>
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Question 1 */}
      {step === 'question1' && (
        <Fade in timeout={600}>
          <Paper
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 2,
              border: '1px solid rgba(34,211,238,0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(34,211,238,0.3)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Question 1 of 2
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.8 }}>
              Is the core focus/major subject of your undergraduate degree computer science,
              computer application or computer engineering?
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Eligible Degrees:
              </Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: 1.8 }}>
                  <li>BCA (Bachelor of Computer Applications)</li>
                  <li>MCA (Master of Computer Applications)</li>
                  <li>BE/B.Tech (Computer Science/Computer Engineering)</li>
                  <li>BSc (Computer Science/Computer Engineering)</li>
                  <li>MSc (Computer Science/Computer Engineering)</li>
                  <li>M.Tech (Computer Science/Computer Engineering)</li>
                </ul>
              </Typography>
            </Alert>

            <Alert severity="warning" sx={{ mb: 4 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Engineering degrees not specifically in the Computer Science domain
                (e.g., Mechanical, Civil, Electrical, Electronics, etc.) are not eligible.
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => handleAnswer(true, 'question2')}
                sx={{
                  minWidth: 150,
                  py: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  color: '#081018',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(16,185,129,0.4)',
                  }
                }}
              >
                Yes
              </Button>

              <Button
                variant="contained"
                size="large"
                startIcon={<Cancel />}
                onClick={() => handleAnswer(false, 'ineligible')}
                sx={{
                  minWidth: 150,
                  py: 2,
                  background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                  color: '#fff',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(239,68,68,0.4)',
                  }
                }}
              >
                No
              </Button>
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Question 2 */}
      {step === 'question2' && (
        <Fade in timeout={600}>
          <Paper
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 2,
              border: '1px solid rgba(34,211,238,0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(34,211,238,0.3)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Question 2 of 2
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.8 }}>
              Are you in your final semester of your degree?
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => handleAnswer(true, 'agreement')}
                sx={{
                  minWidth: 150,
                  py: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  color: '#081018',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(16,185,129,0.4)',
                  }
                }}
              >
                Yes
              </Button>

              <Button
                variant="contained"
                size="large"
                startIcon={<Cancel />}
                onClick={() => handleAnswer(false, 'ineligible')}
                sx={{
                  minWidth: 150,
                  py: 2,
                  background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
                  color: '#fff',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(239,68,68,0.4)',
                  }
                }}
              >
                No
              </Button>
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Agreement */}
      {step === 'agreement' && (
        <Fade in timeout={600}>
          <Paper
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 2,
              border: '1px solid rgba(34,211,238,0.2)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(34,211,238,0.3)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#10b981' }}>
              ✓ Eligibility Confirmed
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
              Congratulations! You meet the eligibility criteria. Before proceeding to the assessments,
              please confirm the following:
            </Typography>

            <Paper sx={{ p: 3, mb: 3, bgcolor: 'rgba(167,139,250,0.05)' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                    sx={{
                      color: track === 'cloud' ? '#60a5fa' : '#34d399',
                      '&.Mui-checked': {
                        color: track === 'cloud' ? '#60a5fa' : '#34d399',
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    I confirm that the information provided is true to the best of my knowledge.
                    I understand that incorrect or dishonest submissions will be disqualified from
                    the recruitment process.
                  </Typography>
                }
              />
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                size="large"
                onClick={onBack}
                sx={{ px: 4, py: 1.5 }}
              >
                Back to Home
              </Button>

              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={handleSubmit}
                disabled={!agreementChecked}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  borderRadius: 4,
                  background: agreementChecked
                    ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f97316 100%)'
                    : 'rgba(100,100,100,0.3)',
                  color: agreementChecked ? '#0a0a0a' : '#666',
                  boxShadow: agreementChecked ? '0 4px 20px rgba(245,158,11,0.3)' : 'none',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': agreementChecked ? {
                    transform: 'translateY(-4px)',
                    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #ea580c 100%)',
                    boxShadow: '0 12px 32px rgba(245,158,11,0.5)',
                  } : {}
                }}
              >
                Proceed to Assessments
              </Button>
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Ineligible */}
      {step === 'ineligible' && (
        <Fade in timeout={600}>
          <Paper
            sx={{
              p: 4,
              mb: 3,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px solid rgba(239,68,68,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(239,68,68,0.3)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Cancel sx={{ fontSize: 80, color: '#ef4444', mb: 2 }} />

            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#ef4444' }}>
              Not Eligible
            </Typography>

            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
              Unfortunately, you do not meet the eligibility criteria for this recruitment process at this time.
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={onBack}
              sx={{
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
                color: '#fff',
              }}
            >
              Back to Home
            </Button>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default EligibilityChecklist;
